import { Access } from "@p0security/shared/types/access/index";

import { CATALOG_KEY_FORMAT } from "./catalog-key.ts";

/** The roles an `admin` request can ask for. */
export const DatabricksAdminRoles = [
  "workspace-admin",
  "account-admin",
] as const;

/**
 * What revoking an `admin` request restores: the user's own workspace
 * assignment before the grant, `none` meaning unassigned. Account admin has
 * nothing to restore, so it is always `none`.
 */
export const DatabricksPriorAssignments = ["none", "USER"] as const;

/** Both accesses grant directly to the requestor's existing Databricks user. */
export const DatabricksAccesses = {
  privilege: Access({
    description:
      "Unity Catalog privileges on a catalog, schema, table, volume or function",
    label: "Privilege",
    maturity: "beta",
    delegation: {},
    generated: {
      userName: {
        type: "string",
        label: "Databricks user",
        description:
          "The email P0 granted the privileges to, which is the Databricks username",
      },
      // Revoke goes back through the account and workspace recorded here, at
      // the URL recorded here, not whatever the catalog item points at by
      // then.
      accountId: {
        type: "string",
        label: "Account ID",
        description: "The Databricks account P0 granted in",
      },
      workspaceId: {
        type: "string",
        label: "Workspace ID",
        description: "The workspace P0 granted through",
      },
      workspaceUrl: {
        type: "string",
        label: "Workspace URL",
        description:
          "The URL of the workspace P0 granted through, which revoking goes back to",
      },
      addedGrants: {
        type: "string",
        label: "Added grants",
        description:
          "The grants P0 added for this request, including the parent USE CATALOG and USE SCHEMA it did not find already held. Revoking removes only these.",
        multivalued: true,
      },
    },
    // Required parameters are CLI positionals, so this reads
    // `privilege <catalog> <privileges..> [--schema] [--table | --volume | --function]`.
    // The deepest securable given is the one granted on.
    parameters: {
      catalog: {
        type: "static",
        label: "Catalog",
        description: `An installed Unity Catalog catalog, by name, or as ${CATALOG_KEY_FORMAT} when two installed catalogs share a name`,
      },
      schema: {
        type: "dynamic",
        label: "Schema",
        description: "A schema in the catalog",
        optional: true,
      },
      table: {
        type: "dynamic",
        label: "Table",
        description: "A table in the schema",
        optional: true,
      },
      volume: {
        type: "dynamic",
        label: "Volume",
        description: "A volume in the schema",
        optional: true,
      },
      function: {
        type: "dynamic",
        label: "Function",
        description: "A function in the schema",
        optional: true,
      },
      privileges: {
        type: "static",
        label: "Privileges",
        // Slack shows the description as the placeholder, which it caps at
        // 150 characters, so the detail goes in the hint.
        description: "Unity Catalog privileges, e.g. SELECT",
        hint: "P0 adds USE_CATALOG and USE_SCHEMA to a request on a table, volume or function, and only USE_CATALOG to one on a schema, so name USE_SCHEMA yourself if you need it there. P0 refuses a request for a privilege you already hold directly on that object.",
        multivalued: true,
      },
      // Set from the parameters above by the privilege lifecycle's
      // `normalize`, so that the approval message says what the privileges
      // are granted on: SELECT on one table and SELECT on a whole catalog
      // would otherwise read the same.
      securable: {
        type: "generated",
        label: "Securable",
        description:
          "What the privileges are granted on, e.g. table main.sales.orders",
      },
      // Derived from the parameters above by `normalize`. The lifecycle grants
      // the union of these across the user's open requests in the catalog.
      grants: {
        type: "generated",
        label: "Grants",
        description:
          "Each privilege the request grants, with its securable, including the USE CATALOG and USE SCHEMA it needs",
        multivalued: true,
      },
    },
    // Reads "privileges SELECT on securable table main.sales.orders in catalog
    // main@<workspace ID>".
    render: {
      instructions: () =>
        "P0 grants these privileges to the Databricks user whose username is your P0 email. They work once you're assigned to a workspace that can use the catalog, and have compute there, such as a SQL warehouse or cluster. P0 doesn't grant either.",
      locator: "catalog",
      policy: "privileges",
      resource: "securable",
    } as const,
  }),
  admin: Access({
    description: "Workspace admin on one workspace, or account admin",
    label: "Admin role",
    maturity: "beta",
    delegation: {},
    generated: {
      userId: {
        type: "string",
        label: "Databricks user ID",
        description: "The account SCIM ID of the requestor's Databricks user",
      },
      priorAssignment: {
        type: "string",
        label: "Prior workspace assignment",
        description:
          "The user's assignment to the workspace before the grant, which revoking restores",
        choices: DatabricksPriorAssignments,
      },
      // Set for workspace admin only. Revoke goes back to the URL recorded
      // here, not whatever the workspace item holds by then.
      workspaceUrl: {
        type: "string",
        label: "Workspace URL",
        description:
          "The URL of the workspace P0 made the user an admin of, which revoking goes back to",
        optional: true,
      },
    },
    // Required parameters are CLI positionals, so this reads
    // `admin <role> [--account] [--workspace]`.
    parameters: {
      role: {
        type: "static",
        label: "Role",
        choices: DatabricksAdminRoles,
      },
      account: {
        type: "static",
        label: "Account",
        description:
          "The installed Databricks account, needed only when more than one is installed",
        isPreselect: true,
        optional: true,
      },
      workspace: {
        type: "dynamic",
        label: "Workspace",
        description:
          "An installed workspace with workspace-admin requests turned on, by ID or by its name in P0",
        optional: true,
      },
      workspaceName: {
        type: "generated",
        label: "Workspace name",
        optional: true,
      },
      scope: {
        type: "generated",
        label: "Scope",
        description:
          "What the role is granted on: the workspace ID for workspace-admin, or the account ID for account-admin",
      },
    },
    render: { policy: "role", resource: "scope" } as const,
  }),
};

export type DatabricksAccesses = typeof DatabricksAccesses;
